// =============================================================================
// components/ui/ParticleBackground.tsx — the ONE animated background primitive.
//
// A premium "aurora" surface: a soft vertical gradient wash + a few large,
// slowly-drifting RADIAL-GRADIENT glow orbs. The orbs are true SVG radial
// gradients (center tint → fully transparent edge) so they read as soft light,
// NOT the hard flat discs of the old version — the difference between "looks
// cheap" and depth. ~60fps via react-native-reanimated (each orb drifts on the
// UI thread with one looping driver — no per-frame JS). Purely decorative
// (pointerEvents="none"). No blur native module needed — the falloff is the SVG
// gradient itself. react-native-svg is already a project dep (Sparkline).
//
// Two theme modes only (design-system rule): 'light' (lavender, default) +
// 'dark' (voice/call). All tints reference the brand palette.
// =============================================================================
import React, { useMemo } from 'react';
import { StyleSheet, useWindowDimensions, ViewStyle } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  withDelay,
  interpolate,
  Easing,
  ReduceMotion,
  cancelAnimation,
} from 'react-native-reanimated';
import LinearGradient from 'react-native-linear-gradient';
import Svg, { Defs, RadialGradient as SvgRadialGradient, Stop, Circle } from 'react-native-svg';
import { colors, colorTokens, animationTokens, ThemeMode } from '../../constants/theme';
import CryptoMesh from './CryptoMesh';

export interface ParticleBackgroundProps {
  /** Theme mode. 'light' (default) or 'dark'. */
  theme?: ThemeMode;
  /** Number of drifting glow orbs. Keep small for perf. Default 5. */
  count?: number;
  /** Overall opacity of the glow layer. Default 1. */
  intensity?: number;
  /** Overlay the decentralized crypto node-mesh (brand identity). Default true.
   *  Pass false on dense surfaces (chat) where texture competes with content. */
  mesh?: boolean;
  /** Mesh node count / opacity. Defaults 14 / 0.85. */
  meshCount?: number;
  meshIntensity?: number;
  style?: ViewStyle;
}

// Deterministic pseudo-random so layout is stable across renders (no flicker).
function seeded(i: number, salt: number) {
  const x = Math.sin((i + 1) * 12.9898 + salt * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

// Orb tints: [r,g,b, centerAlpha]. The radial fades this to fully transparent.
const ORB_TINTS_LIGHT: [number, number, number, number][] = [
  [108, 92, 231, 0.30],  // primary
  [168, 85, 247, 0.26],  // primaryLight
  [255, 100, 150, 0.16], // warm pink accent
  [45, 212, 191, 0.12],  // cool teal accent
];
const ORB_TINTS_DARK: [number, number, number, number][] = [
  [168, 85, 247, 0.42],
  [108, 92, 231, 0.34],
  [45, 212, 191, 0.16],
  [255, 255, 255, 0.06],
];

function Orb({
  index,
  theme,
  width,
  height,
  tints,
  paused,
}: {
  index: number;
  theme: ThemeMode;
  width: number;
  height: number;
  tints: [number, number, number, number][];
  /** When true (screen unfocused), the drift loop is frozen to save battery/GPU. */
  paused: boolean;
}) {
  // Large, soft orbs (glow reaches well past the visible core).
  const size = 220 + seeded(index, 1) * 260; // 220–480
  const startX = seeded(index, 2) * width - size / 2;
  const startY = seeded(index, 3) * height - size / 2;
  // Elliptical orbit radii — each orb travels a continuous loop, not back-and-forth.
  const radiusX = 90 + seeded(index, 4) * 120;  // 90–210
  const radiusY = 80 + seeded(index, 5) * 120;  // 80–200
  const phase = seeded(index, 8) * Math.PI * 2; // desync orbs around the loop
  const [r, g, b, centerA] = tints[index % tints.length];
  const dur = 9000 + seeded(index, 6) * 7000; // 9–16s per FULL orbit — felt but smooth
  const delay = seeded(index, 7) * 1500;
  const gid = `orb-${theme}-${index}`;
  const rgb = `rgb(${r},${g},${b})`;

  const t = useSharedValue(0);
  React.useEffect(() => {
    // Pause the perpetual drift when the screen is unfocused: an infinite
    // withRepeat otherwise keeps burning UI-thread frames + GPU on the ~50 screens
    // this background lives behind, even off-screen. cancelAnimation freezes t at
    // its current value (the orb holds its position); on re-focus we re-arm the
    // loop from wherever it froze so it resumes seamlessly, not from the start.
    if (paused) {
      cancelAnimation(t);
      return;
    }
    // Linear, NON-reversing repeat → constant-speed, seamless INFINITE loop. The
    // old yoyo (reverse=true + sin easing) decelerated to zero at both extremes,
    // so the surface read as static. cos/sin are periodic, so t=0 and t=1 line up
    // with no jump. Runs on the UI thread → 60fps, no per-frame JS.
    // reduceMotion: Never → the ambient background keeps moving even when the OS
    // "Reduce Motion" accessibility setting is on (reanimated 3.5+ freezes timing
    // animations by default under Reduce Motion, which read as a dead background).
    // Resuming from a frozen t: the remaining fraction of THIS cycle plays once,
    // then the seamless infinite repeat takes over — no visible jump.
    const remaining = Math.max(0, 1 - t.value);
    t.value = withDelay(
      delay,
      withTiming(1, { duration: dur * remaining, easing: Easing.linear, reduceMotion: ReduceMotion.Never }, (finished) => {
        'worklet';
        if (finished) {
          t.value = 0;
          t.value = withRepeat(
            withTiming(1, { duration: dur, easing: Easing.linear, reduceMotion: ReduceMotion.Never }),
            -1,
            false,
          );
        }
      }),
    );
  }, [t, dur, delay, paused]);

  const aStyle = useAnimatedStyle(() => {
    const a = t.value * 2 * Math.PI + phase;
    return {
      transform: [
        { translateX: Math.cos(a) * radiusX },
        { translateY: Math.sin(a) * radiusY },
        // Gentle continuous swell + shimmer, both full-period so they loop seamlessly.
        { scale: 1 + 0.12 * Math.sin(a) },
      ],
      opacity: 0.8 + 0.2 * Math.cos(a),
    };
  });

  return (
    <Animated.View
      pointerEvents="none"
      style={[{ position: 'absolute', left: startX, top: startY, width: size, height: size }, aStyle]}
    >
      <Svg width={size} height={size}>
        <Defs>
          <SvgRadialGradient id={gid} cx="50%" cy="50%" r="50%">
            <Stop offset="0%" stopColor={rgb} stopOpacity={centerA} />
            <Stop offset="55%" stopColor={rgb} stopOpacity={centerA * 0.35} />
            <Stop offset="100%" stopColor={rgb} stopOpacity={0} />
          </SvgRadialGradient>
        </Defs>
        <Circle cx={size / 2} cy={size / 2} r={size / 2} fill={`url(#${gid})`} />
      </Svg>
    </Animated.View>
  );
}

export default function ParticleBackground({
  theme = 'light',
  count = 5,
  intensity = 1,
  mesh = true,
  meshCount = 14,
  meshIntensity = 0.85,
  style,
}: ParticleBackgroundProps) {
  const { width, height } = useWindowDimensions();
  // Focus-aware: freeze the perpetual orb drift AND skip the heavier node-mesh
  // layer when this screen isn't the focused route (it's rendered behind ~50
  // screens; only the visible one should spend battery/GPU). Resumes on focus.
  // All real call sites live inside the navigator, so useIsFocused is safe here.
  const focused = useIsFocused();
  const dark = theme === 'dark';
  const tc = colorTokens[theme];
  const tints = dark ? ORB_TINTS_DARK : ORB_TINTS_LIGHT;

  // Calmer vertical wash (top slightly lifted → base → faint tint at the bottom)
  // gives a cleaner, more premium base than the old diagonal 3-stop.
  const gradientColors: [string, string, string] = dark
    ? ['#181127', tc.bg, '#120C20']
    : ['#F1ECFA', colors.bg, '#ECE4F7'];

  const orbs = useMemo(
    () => Array.from({ length: Math.max(0, count) }, (_, i) => i),
    [count],
  );

  return (
    <Animated.View
      pointerEvents="none"
      style={[StyleSheet.absoluteFill, { opacity: intensity }, style]}
    >
      <LinearGradient
        colors={gradientColors}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      {orbs.map((i) => (
        <Orb key={i} index={i} theme={theme} width={width} height={height} tints={tints} paused={!focused} />
      ))}
      {/* Decentralized node-mesh — the crypto/blockchain design language, now on
          every screen that uses this background. Static + subtle; opt out with
          mesh={false}. Skipped while unfocused so an off-screen background doesn't
          keep an SVG node/edge layer mounted on ~50 screens (mounts back on focus). */}
      {mesh && focused && <CryptoMesh theme={theme} count={meshCount} intensity={meshIntensity} />}
    </Animated.View>
  );
}

// Motion note: withTiming durations above are intentionally long (ambient). For
// entrance/press motion elsewhere use animationTokens.spring / .duration.
export const particleMotion = animationTokens;
